import { useMemo, useState, type MouseEvent } from 'react';
import { AxiosError } from 'axios';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  Paper,
} from '@mui/material';
import { useI18n } from '@/i18n';
import { useAuth } from '@/auth/AuthProvider';

const drawerWidth = 292;
const appBarHeight = { xs: 64, lg: 80 };

const baseNavigationItems = [
  { key: 'dashboard', path: '/dashboard', initials: 'DB' },
  { key: 'assets', path: '/assets', initials: 'AS' },
  { key: 'scan', path: '/scan', initials: 'SC' },
  { key: 'locations', path: '/locations', initials: 'LC' },
  { key: 'inventory', path: '/inventory', initials: 'IV' },
  { key: 'reports', path: '/reports', initials: 'RP' },
  { key: 'users', path: '/users', initials: 'US', adminOnly: true },
  { key: 'admin', path: '/admin', initials: 'AD', label: 'Panel admina', adminOnly: true },
  { key: 'settings', path: '/settings', initials: 'ST' },
];

export default function EnterpriseShell() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileTeam, setProfileTeam] = useState('');
  const [profilePassword, setProfilePassword] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, logout, updateProfile } = useAuth();

  const activePath = useMemo(() => location.pathname, [location.pathname]);
  const navigationItems = useMemo(
    () => baseNavigationItems.filter((item) => {
      if (user?.role === 'Admin') return true;
      if (user?.role === 'Manager') return item.key !== 'admin';
      return ['scan', 'inventory', 'settings'].includes(item.key);
    }),
    [user?.role],
  );

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const openUserMenu = (event: MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const closeUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const openProfileDialog = () => {
    setProfileName(user?.name ?? '');
    setProfileEmail(user?.email ?? '');
    setProfileTeam(user?.team ?? '');
    setProfilePassword('');
    setProfileError('');
    setProfileSuccess('');
    setProfileOpen(true);
    closeUserMenu();
  };

  const closeProfileDialog = () => {
    setProfileOpen(false);
  };

  const handleProfileSave = async () => {
    if (!profileName.trim() || !profileEmail.trim()) {
      setProfileError('Imię i login są wymagane.');
      return;
    }

    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      await updateProfile({
        name: profileName.trim(),
        email: profileEmail.trim(),
        team: profileTeam.trim() ? profileTeam.trim() : null,
        password: profilePassword.trim() ? profilePassword : undefined,
      });
      setProfilePassword('');
      setProfileSuccess('Zapisano zmiany użytkownika.');
    } catch (error) {
      let message = 'Nie udało się zapisać zmian użytkownika.';
      if (error instanceof AxiosError) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string' && detail.trim()) {
          message = detail;
        }
      }
      setProfileError(message);
    } finally {
      setProfileSaving(false);
    }
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, py: 2, flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        <Typography variant="overline" color="text.secondary" sx={{ px: 1.5 }}>
          {t('navigation.title')}
        </Typography>
        <List disablePadding sx={{ mt: 1 }}>
          {navigationItems.map((item) => {
            const isActive = item.path === '/dashboard' ? activePath === '/dashboard' : activePath.startsWith(item.path);
            const label = item.label ?? t(`navigation.${item.key}`);

            return (
              <ListItemButton
                key={item.path}
                selected={isActive}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  minHeight: 48,
                  mb: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    background: 'linear-gradient(90deg, rgba(20, 63, 107, 0.14), rgba(15, 118, 110, 0.12))',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 12,
                      fontWeight: 800,
                      bgcolor: isActive ? 'primary.main' : 'rgba(148, 163, 184, 0.18)',
                      color: isActive ? '#fff' : 'text.secondary',
                    }}
                  >
                    {item.initials}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  sx={{ minWidth: 0 }}
                  primaryTypographyProps={{ fontWeight: isActive ? 700 : 600, noWrap: true }}
                />
              </ListItemButton>
            );
          })}
        </List>

      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 2 }}>
        <Button fullWidth variant="outlined" onClick={logout}>
          Wyloguj
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          left: 0,
          width: '100%',
        }}
      >
        <Toolbar disableGutters sx={{ gap: { xs: 1, md: 2 }, pr: { xs: 1.5, md: 3 }, minHeight: `${appBarHeight.xs}px`, '@media (min-width:1200px)': { minHeight: `${appBarHeight.lg}px` } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { lg: 'none' } }}
          >
            <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.5 }}>
              <Box component="span" sx={{ width: 18, height: 2, borderRadius: 999, bgcolor: 'currentColor' }} />
              <Box component="span" sx={{ width: 18, height: 2, borderRadius: 999, bgcolor: 'currentColor' }} />
              <Box component="span" sx={{ width: 18, height: 2, borderRadius: 999, bgcolor: 'currentColor' }} />
            </Box>
          </IconButton>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              minWidth: 0,
              flexShrink: 0,
              width: { xs: 'auto', lg: `${drawerWidth}px` },
              px: { xs: 0, lg: 2.5 },
            }}
          >
            <Box
              component={RouterLink}
              to="/scan"
              aria-label="myZenor"
              title={t('scan.title')}
              sx={{
                width: { xs: 48, sm: 54 },
                height: { xs: 48, sm: 54 },
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '50%',
                bgcolor: 'transparent',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              <Box
                component="img"
                src="/myzenor-logo.png"
                alt=""
                sx={{
                  position: 'absolute',
                  width: { xs: 98, sm: 110 },
                  maxWidth: 'none',
                  height: 'auto',
                  display: 'block',
                  left: { xs: -25, sm: -28 },
                  top: { xs: -15, sm: -17 },
                }}
              />
            </Box>
            <Stack spacing={0.15} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: { xs: 13, sm: 15 } }}>
                Varibox Control Center
              </Typography>
              <Typography
                component="a"
                href="mailto:marcin.chojan@icloud.com"
                aria-label="Napisz do myZenor"
                variant="caption"
                noWrap
                sx={{ opacity: 0.88, lineHeight: 1.1, color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                by <Box component="span" sx={{ fontWeight: 800, color: '#8fdf55' }}>myZenor</Box>
              </Typography>
            </Stack>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              px: 1.5,
              py: 0.75,
              flex: '1 1 auto',
              maxWidth: 560,
              borderRadius: 999,
              backgroundColor: 'rgba(255, 255, 255, 0.14)',
              borderColor: 'rgba(255, 255, 255, 0.18)',
            }}
          >
            <InputBase
              placeholder={t('navigation.searchPlaceholder')}
              sx={{ flex: 1, color: 'inherit', fontSize: 14 }}
            />
          </Paper>

          <Box sx={{ flexGrow: 1 }} />

          <Chip label={t('navigation.productionMode')} size="small" sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' }} />

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ cursor: 'pointer', flexShrink: 0 }}
            onClick={openUserMenu}
          >
            <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {user?.name ?? 'Administrator'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {user?.role ?? t('navigation.userRole')}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.16)' }}>{(user?.name ?? 'AF').slice(0, 2).toUpperCase()}</Avatar>
          </Stack>
        </Toolbar>
      </AppBar>

      <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={closeUserMenu}>
        <MenuItem onClick={openProfileDialog}>Mój profil</MenuItem>
        <MenuItem
          onClick={() => {
            closeUserMenu();
            logout();
          }}
        >
          Wyloguj
        </MenuItem>
      </Menu>

      <Dialog open={profileOpen} onClose={closeProfileDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edytuj profil</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {profileError ? <Typography color="error.main">{profileError}</Typography> : null}
            {profileSuccess ? <Typography color="success.main">{profileSuccess}</Typography> : null}

            <TextField
              label="Imię"
              value={profileName}
              onChange={(event) => setProfileName(event.target.value)}
              disabled={profileSaving}
              fullWidth
            />

            <TextField
              label="Login"
              value={profileEmail}
              onChange={(event) => setProfileEmail(event.target.value)}
              disabled={profileSaving}
              fullWidth
            />

            <TextField
              label="Zespół"
              value={profileTeam}
              onChange={(event) => setProfileTeam(event.target.value)}
              disabled={profileSaving}
              fullWidth
            />

            <TextField
              label="Nowe hasło (opcjonalnie)"
              type="password"
              value={profilePassword}
              onChange={(event) => setProfilePassword(event.target.value)}
              disabled={profileSaving}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProfileDialog} disabled={profileSaving}>Zamknij</Button>
          <Button variant="contained" onClick={handleProfileSave} disabled={profileSaving}>Zapisz</Button>
        </DialogActions>
      </Dialog>

      <Box component="nav" sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}>
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop || mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: isDesktop ? `${appBarHeight.lg}px` : 0,
              height: isDesktop ? `calc(100% - ${appBarHeight.lg}px)` : '100%',
              borderRight: '1px solid rgba(148, 163, 184, 0.18)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flex: '1 1 0%',
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          px: { xs: 2, md: 3 },
          pb: 4,
        }}
      >
        <Toolbar sx={{ minHeight: `${appBarHeight.xs}px`, '@media (min-width:1200px)': { minHeight: `${appBarHeight.lg}px` } }} />
        <Outlet />
      </Box>
    </Box>
  );
}